/**
 * The page signs the visitor in as a contact: an AXL site form, a quiz or a checkout ends in the pixel's
 * `ao.setContactId`, which writes the `a_c` cookie and fires this event on `window` (`client/site/src/track/track.ts`;
 * a form in a widget iframe on another site gets it from the host script, `client/site/src/widget/widget.ts`).
 * `detail.contactId` is the contact's id.
 */
export const CONTACT_EVENT = 'axl:contact';

const CONTACT_COOKIE = 'a_c';
/** The host script of an AXL form embedded on another site keeps its cookies under this prefix. */
const WIDGET_COOKIE_PREFIX = 'widget_';
const CONTACT_ID_RE = /^[A-Za-z0-9_-]{16,40}$/;

type PageGlobals = Window & {
    ao?: { currentContactId?: string | null };
    AXLData?: { contactId?: string | null };
};

/**
 * The contact the page knows now: the pixel's current one (set the moment a form or a checkout answers), the `a_c`
 * cookie (also set by the server from a `?a_c=` link), its `widget_a_c` copy on a site with an embedded AXL form, or
 * the one the AXL page was rendered with.
 */
export function pageContactId(win: Window): string | null {
    const page = win as PageGlobals;
    let id: string | null | undefined;
    try {
        const cookies = win.document.cookie;
        id = page.ao?.currentContactId
            || readCookie(cookies, CONTACT_COOKIE)
            || readCookie(cookies, WIDGET_COOKIE_PREFIX + CONTACT_COOKIE)
            || page.AXLData?.contactId;
    } catch {
        // A sandboxed frame may refuse the cookie jar: then the page simply knows nobody.
        id = null;
    }
    return validContactId(id);
}

export function validContactId(id: unknown): string | null {
    return typeof id === 'string' && CONTACT_ID_RE.test(id.trim()) ? id.trim() : null;
}

function readCookie(cookies: string, name: string): string | null {
    for (const part of cookies.split(';')) {
        const eq = part.indexOf('=');
        if (eq < 0 || part.slice(0, eq).trim() !== name)
            continue;
        try {
            return decodeURIComponent(part.slice(eq + 1).trim());
        } catch {
            return null;
        }
    }
    return null;
}
